import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiErrorMessage, launchApi } from '../api';
import { chipButtonSx, launchColors } from '../launchTheme';
import { CUSTOMER_CHAINS, CustomerCategory, CustomerChain, CustomerContact } from './customerContacts';

interface Props {
  open: boolean;
  contacts: CustomerContact[];
  canEdit: boolean;
  defaultChain: CustomerChain | '';
  onClose: () => void;
  onChanged: (contacts: CustomerContact[]) => void;
}

type Draft = Omit<CustomerContact, 'id'>;
type ChainFilter = 'alla' | CustomerChain;

const emptyCategory = (): CustomerCategory => ({ name: '', packageMark: '' });

const emptyDraft = (chain: CustomerChain | ''): Draft => ({
  chain: chain || 'Ica',
  name: '',
  email: '',
  phone: '',
  address: '',
  categories: [],
  note: '',
});

const draftFrom = (contact: CustomerContact): Draft => ({
  chain: contact.chain,
  name: contact.name,
  email: contact.email,
  phone: contact.phone,
  address: contact.address,
  categories: contact.categories.map((category) => ({ ...category })),
  note: contact.note,
});

const categorySummary = (contact: CustomerContact) => contact.categories
  .map((category) => (category.name ? (category.packageMark ? `${category.name} (${category.packageMark})` : category.name) : category.packageMark))
  .filter(Boolean)
  .join(' · ');

const matchesQuery = (contact: CustomerContact, query: string) => {
  if (!query) return true;
  const haystack = [
    contact.chain,
    contact.name,
    contact.email,
    contact.phone,
    contact.address,
    contact.note,
    ...contact.categories.flatMap((category) => [category.name, category.packageMark]),
  ].join(' ').toLocaleLowerCase('sv');
  return haystack.includes(query);
};

const CustomerInfoDialog: React.FC<Props> = ({ open, contacts, canEdit, defaultChain, onClose, onChanged }) => {
  const [chainFilter, setChainFilter] = useState<ChainFilter>('alla');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft(defaultChain));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const needle = query.trim().toLocaleLowerCase('sv');
  const visible = contacts.filter((contact) => (chainFilter === 'alla' || contact.chain === chainFilter) && matchesQuery(contact, needle));
  const newChain = chainFilter === 'alla' ? defaultChain : chainFilter;

  useEffect(() => {
    if (!open) return;
    setExpanded(false);
    setDraft(emptyDraft(defaultChain));
    setError(null);
    setQuery('');
  }, [defaultChain, open]);

  const expand = (id: string, next: Draft) => {
    setExpanded(id);
    setDraft(next);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = expanded && expanded !== 'new'
        ? await launchApi.updateCustomerContact(expanded, draft)
        : await launchApi.createCustomerContact(draft);
      onChanged(result.contacts);
      setExpanded(false);
      setDraft(emptyDraft(newChain));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Kontakten kunde inte sparas.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (contact: CustomerContact) => {
    setBusy(true);
    setError(null);
    try {
      const result = await launchApi.deleteCustomerContact(contact.id);
      onChanged(result.contacts);
      if (expanded === contact.id) {
        setExpanded(false);
        setDraft(emptyDraft(newChain));
      }
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Kontakten kunde inte tas bort.'));
    } finally {
      setBusy(false);
    }
  };

  const form = (contactId: string | null) => (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <TextField select label="Kedja" value={draft.chain} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, chain: event.target.value as CustomerChain })}>
          {CUSTOMER_CHAINS.map((chain) => <MenuItem key={chain} value={chain}>{chain}</MenuItem>)}
        </TextField>
        <TextField label="Ansvarig" value={draft.name} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
        <TextField label="E-post" value={draft.email} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, email: event.target.value })} />
        <TextField label="Telefonnummer" value={draft.phone} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} />
        <TextField label="Leveransadress" value={draft.address} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, address: event.target.value })} sx={{ gridColumn: { sm: '1 / -1' } }} />
        <TextField label="Övrig info" value={draft.note} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, note: event.target.value })} sx={{ gridColumn: { sm: '1 / -1' } }} />
      </Box>
      <Stack spacing={1}>
        {draft.categories.map((category, index) => (
          <Stack key={`${contactId || 'new'}-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField label="Kategori" value={category.name} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, categories: draft.categories.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) })} sx={{ flex: 1 }} />
            <TextField label="Märk paketet med" value={category.packageMark} disabled={!canEdit} onChange={(event) => setDraft({ ...draft, categories: draft.categories.map((item, itemIndex) => itemIndex === index ? { ...item, packageMark: event.target.value } : item) })} sx={{ flex: 1 }} />
            {canEdit && (
              <IconButton aria-label="Ta bort kategori" onClick={() => setDraft({ ...draft, categories: draft.categories.filter((_, itemIndex) => itemIndex !== index) })}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
        {canEdit && (
          <Button startIcon={<AddIcon />} onClick={() => setDraft({ ...draft, categories: [...draft.categories, emptyCategory()] })} sx={{ alignSelf: 'flex-start' }}>
            Lägg till kategori
          </Button>
        )}
      </Stack>
      {canEdit && (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" disabled={busy} onClick={() => { void save(); }}>{contactId ? 'Spara ändring' : 'Lägg till'}</Button>
          {contactId && <Button color="inherit" disabled={busy} onClick={() => { const contact = contacts.find((item) => item.id === contactId); if (contact) void remove(contact); }}>Ta bort</Button>}
        </Stack>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Kundinformation</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField size="small" label="Sök i kundkorten" value={query} onChange={(event) => setQuery(event.target.value)} />
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {(['alla', ...CUSTOMER_CHAINS] as ChainFilter[]).map((chain) => (
              <Button key={chain} sx={chipButtonSx(chainFilter === chain)} onClick={() => setChainFilter(chain)}>
                {chain === 'alla' ? 'Alla' : chain}
              </Button>
            ))}
          </Stack>
          {canEdit && (
            <Accordion disableGutters expanded={expanded === 'new'} onChange={(_event, isExpanded) => { if (isExpanded) expand('new', emptyDraft(newChain)); else setExpanded(false); }} sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '8px', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700, color: launchColors.navy }}>Ny kontakt</Typography>
              </AccordionSummary>
              <AccordionDetails>{expanded === 'new' && form(null)}</AccordionDetails>
            </Accordion>
          )}
          {visible.length === 0 && <Typography sx={{ color: launchColors.textSecondary }}>Inga kontakter matchar.</Typography>}
          {visible.map((contact) => (
            <Accordion key={contact.id} disableGutters expanded={expanded === contact.id} onChange={(_event, isExpanded) => { if (isExpanded) expand(contact.id, draftFrom(contact)); else setExpanded(false); }} sx={{ border: `1px solid ${launchColors.divider}`, borderRadius: '8px', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: launchColors.navy }}>{contact.name}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, color: launchColors.textSecondary }}>
                    {[contact.chain, categorySummary(contact) || contact.email || contact.phone].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>{expanded === contact.id && form(contact.id)}</AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerInfoDialog;
